import type { KeyboardEvent, ReactNode } from "react"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { t } from "@/lib/i18n"
export type MenuGridItemExtra = {
    id: number
    name: string
    price: string
    status: string
}

export type MenuGridItemExtraGroup = {
    id: number
    name: string
    minSelect: number
    maxSelect: number | null
    extras: MenuGridItemExtra[]
}

export type MenuGridItem = {
    id: number
    name: string
    description: string | null
    price: string
    currency: string
    imageUrl: string | null
    status?: string
    station?: string
    /** False when a required extra group has no orderable extras. Set on staff order surfaces; absent (and treated as available) elsewhere. */
    available?: boolean
    extraGroups?: MenuGridItemExtraGroup[]
}
export type MenuGridCategory = {
    id: number
    name: string
    items: MenuGridItem[]
}
type MenuGridProps = {
    categories: MenuGridCategory[]
    orphanItems?: MenuGridItem[]
    emptyStateMessage?: string
    renderItemAction?: (item: MenuGridItem) => ReactNode
    selectedItemId?: number | null
    onItemClick?: (item: MenuGridItem) => void
    compact?: boolean
    showImages?: boolean
    cartCounts?: Record<number, number>
}
export function formatPrice(price: string, currency: string) {
    const amount = Number(price)
    if (Number.isNaN(amount)) {
        return `${price} ${currency.toUpperCase()}`
    }
    return new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: currency.toUpperCase(),
    }).format(amount)
}
function ItemCard({ item, renderItemAction, selected = false, onItemClick, compact = false, showImages = true, cartCount = 0, }: {
    item: MenuGridItem
    renderItemAction?: (item: MenuGridItem) => ReactNode
    selected?: boolean
    onItemClick?: (item: MenuGridItem) => void
    compact?: boolean
    showImages?: boolean
    cartCount?: number
}) {
    const isSoldOut = item.status === "sold_out"
    // A required group with no orderable extras can't be turned into an order, so
    // it gets the sold-out treatment: greyed and non-clickable. Opening it would
    // strand the waiter on a form that can never submit.
    const isUnavailable = item.available === false
    const isBlocked = isSoldOut || isUnavailable
    const isClickable = typeof onItemClick === "function" && !isBlocked
    const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        if (!isClickable)
            return
        if (event.key !== "Enter" && event.key !== " ")
            return
        event.preventDefault()
        onItemClick(item)
    }
    return (<article className={cn("relative flex h-full flex-col rounded-lg border bg-card shadow-xs", compact ? "gap-2 p-3" : "gap-3 p-4", isClickable && "cursor-pointer transition-colors hover:bg-muted/30 focus-visible:outline-none", isBlocked && "opacity-70", selected && "border-primary ring-2 ring-primary/30")} role={isClickable ? "button" : undefined} tabIndex={isClickable ? 0 : undefined} aria-pressed={isClickable ? selected : undefined} onClick={isClickable ? () => onItemClick(item) : undefined} onKeyDown={handleKeyDown} data-testid={`menu-grid-item-${item.id}`}>
      {cartCount > 0 && (<span className="absolute top-2 right-2 inline-flex items-center rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground">
          × {cartCount}
        </span>)}
      {showImages && (<>
          {item.imageUrl ? (<img src={item.imageUrl} alt={item.name} className="h-36 w-full rounded-md object-cover"/>) : (<div className="flex h-36 w-full items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">{t("ui.react.components.features.menu.menu_grid.image")}</div>)}
        </>)}

      <div className={cn(compact ? "space-y-0.5" : "space-y-1")}>
        <h3 className={cn("font-semibold leading-tight", compact && "text-sm")}>{item.name}</h3>
        {(item.description || !compact) && (<p className={cn("text-sm text-muted-foreground", compact ? "line-clamp-1" : "line-clamp-2")}>
            {item.description || t("ui.menu.no_description")}
          </p>)}
      </div>

      <div className="flex flex-wrap gap-2">
        {item.station ? (<Badge variant="outline">{item.station.replace(/_/g, " ")}</Badge>) : null}
        {item.status === "sold_out" ? (<Badge variant="destructive">{t("ui.react.components.features.menu.menu_grid.soldout")}</Badge>) : null}
        {isUnavailable && !isSoldOut ? (<Badge variant="secondary">{t("ui.react.components.features.menu.menu_grid.unavailable")}</Badge>) : null}
      </div>

      <p className={cn("font-medium", compact ? "text-xs" : "text-sm")}>
        {formatPrice(item.price, item.currency)}
      </p>

      {renderItemAction ? (<div className="mt-auto">
          {renderItemAction(item)}
        </div>) : null}
    </article>)
}
function CategorySection({ category, renderItemAction, selectedItemId, onItemClick, compact = false, showImages = true, cartCounts, }: {
    category: MenuGridCategory
    renderItemAction?: (item: MenuGridItem) => ReactNode
    selectedItemId?: number | null
    onItemClick?: (item: MenuGridItem) => void
    compact?: boolean
    showImages?: boolean
    cartCounts?: Record<number, number>
}) {
    if (category.items.length === 0)
        return null
    return (<section className="space-y-4" data-testid={`menu-category-${category.id}`}>
      <div>
        <h2 className="font-semibold">{category.name}</h2>
        <p className="text-muted-foreground text-sm">
          {category.items.length} {t("ui.menu.available_item", { count: category.items.length })}
        </p>
      </div>

      <div className={cn("grid grid-cols-1 sm:grid-cols-2", compact ? "gap-3 lg:grid-cols-3 xl:grid-cols-4" : "gap-4 lg:grid-cols-3 xl:grid-cols-4")}>
        {category.items.map((item) => (<ItemCard key={item.id} item={item} renderItemAction={renderItemAction} selected={selectedItemId === item.id} onItemClick={onItemClick} compact={compact} showImages={showImages} cartCount={cartCounts?.[item.id] ?? 0}/>))}
      </div>
    </section>)
}
export function MenuGrid({ categories, orphanItems = [], emptyStateMessage = t("ui.menu.no_items_available"), renderItemAction, selectedItemId, onItemClick, compact = false, showImages = true, cartCounts, }: MenuGridProps) {
    const hasItems = categories.some((category) => category.items.length > 0) || orphanItems.length > 0
    return (<>
      {!hasItems && (<div className="rounded-lg border border-dashed p-8 text-sm text-muted-foreground" data-testid="staff-menu-empty-state">
          {emptyStateMessage}
        </div>)}

      {categories.map((category) => (<CategorySection key={category.id} category={category} renderItemAction={renderItemAction} selectedItemId={selectedItemId} onItemClick={onItemClick} compact={compact} showImages={showImages} cartCounts={cartCounts}/>))}

      {orphanItems.length > 0 && (<section className="space-y-4" data-testid="staff-menu-orphan-items">
          <div>
            <h2 className="text-xl font-semibold">{t("ui.react.components.features.menu.menu_grid.category")}</h2>
            <p className="text-sm text-muted-foreground">
              {orphanItems.length} {t("ui.menu.available_item", { count: orphanItems.length })}
            </p>
          </div>
          <div className={cn("grid grid-cols-1 sm:grid-cols-2", compact ? "gap-3 lg:grid-cols-3 xl:grid-cols-4" : "gap-4 lg:grid-cols-3 xl:grid-cols-4")}>
            {orphanItems.map((item) => (<ItemCard key={item.id} item={item} renderItemAction={renderItemAction} selected={selectedItemId === item.id} onItemClick={onItemClick} compact={compact} showImages={showImages} cartCount={cartCounts?.[item.id] ?? 0}/>))}
          </div>
        </section>)}
    </>)
}
